import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, ShieldCheck, IndianRupee, CheckCircle2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || "918055592955").replace(/\D/g, "");
const RZP_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

function inr(paise) {
  return `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;
}

async function loadRazorpayScript() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RZP_SCRIPT_URL;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/**
 * Full-screen modal: collects name/phone/email, creates the order,
 * initiates Razorpay Checkout, verifies signature and shows a success state.
 * Falls back gracefully in MOCK mode (no Razorpay keys configured on server).
 */
export default function OrderNowModal({ open, onClose, service }) {
  const [step, setStep] = useState("form"); // form | paying | success
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [paidOrder, setPaidOrder] = useState(null);

  const reset = () => {
    setStep("form"); setName(""); setPhone(""); setEmail("");
    setBusy(false); setError(""); setPaidOrder(null);
  };

  const close = () => { reset(); onClose(); };

  const startPayment = async () => {
    setError("");
    if (name.trim().length < 2) return setError("Please enter your full name.");
    if (phone.replace(/\D/g, "").length < 10) return setError("Please enter a valid 10-digit phone number.");
    setBusy(true);
    try {
      // 1) Create the form-hub order
      const orderRes = await fetch(`${API}/form-hub/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_slug: service.slug,
          customer_name: name.trim(),
          customer_phone: phone.trim(),
          customer_email: email.trim() || null,
          source: "web_form",
        }),
      });
      if (!orderRes.ok) throw new Error((await orderRes.json()).detail || "Could not create order");
      const order = await orderRes.json();

      // 2) Create the Razorpay payment intent
      const payRes = await fetch(`${API}/form-hub/orders/${order.order_number}/pay`, { method: "POST" });
      if (!payRes.ok) throw new Error((await payRes.json()).detail || "Could not initiate payment");
      const pay = await payRes.json();

      // 3) In MOCK mode (no live keys on server), skip Razorpay checkout entirely and
      //    directly call verify — flip the order to paid so the flow is demo-able.
      if (pay.mock) {
        setStep("paying");
        const v = await fetch(`${API}/form-hub/orders/${order.order_number}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ razorpay_order_id: pay.order_id }),
        });
        if (!v.ok) throw new Error((await v.json()).detail || "Verification failed");
        setPaidOrder(await v.json());
        setStep("success");
        setBusy(false);
        return;
      }

      // 4) LIVE mode — load Razorpay Checkout and open the drop-in
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Payment SDK failed to load. Please check your internet and try again.");

      setStep("paying");
      const rzp = new window.Razorpay({
        key: pay.key_id,
        amount: pay.amount,
        currency: pay.currency,
        name: "UID Infotech",
        description: pay.service_name,
        order_id: pay.order_id,
        prefill: {
          name: pay.customer.name,
          contact: pay.customer.phone,
          email: pay.customer.email || "",
        },
        theme: { color: "#10b981" },
        handler: async (response) => {
          try {
            const v = await fetch(`${API}/form-hub/orders/${order.order_number}/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            if (!v.ok) throw new Error((await v.json()).detail || "Verification failed");
            setPaidOrder(await v.json());
            setStep("success");
          } catch (e) {
            setError(e.message || "Payment verification failed. Please contact support.");
            setStep("form");
          } finally { setBusy(false); }
        },
        modal: {
          ondismiss: () => { setStep("form"); setBusy(false); },
        },
      });
      rzp.on("payment.failed", (resp) => {
        setError(resp?.error?.description || "Payment failed. Please try again.");
        setStep("form");
        setBusy(false);
        toast.error("Payment failed");
      });
      rzp.open();
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="order-now-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#0B1220] shadow-2xl overflow-hidden">
        {/* header */}
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-300">Order · Form-Filling Hub</div>
            <h3 className="mt-1 text-lg font-bold truncate">{service.name}</h3>
          </div>
          <button onClick={close} aria-label="Close" className="text-white/50 hover:text-white shrink-0" data-testid="order-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {step === "form" && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2.5">
              <span className="text-sm text-white/70">You will pay</span>
              <span className="inline-flex items-center gap-1 text-xl font-extrabold text-emerald-300">
                <IndianRupee className="h-4 w-4" /> {inr(service.price_paise).replace("₹", "")}
              </span>
            </div>
            <div className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Full name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Ravi Kumar"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50"
                  data-testid="order-modal-name"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Phone (WhatsApp)</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit mobile number"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50"
                  data-testid="order-modal-phone"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Email <span className="text-white/30">(optional)</span></span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50"
                  data-testid="order-modal-email"
                />
              </label>
            </div>
            {error && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200" data-testid="order-modal-error">
                {error}
              </div>
            )}
            <div className="text-[11px] text-white/45 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
              Secure payment via Razorpay · we never store your card details
            </div>
            <button
              onClick={startPayment}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 px-5 py-3 text-sm font-bold text-white shadow-lg transition-colors"
              data-testid="order-modal-pay-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
              Pay {inr(service.price_paise)} & Confirm Order
            </button>
          </div>
        )}

        {step === "paying" && (
          <div className="p-8 text-center">
            <Loader2 className="mx-auto h-10 w-10 text-emerald-400 animate-spin" />
            <div className="mt-4 text-sm font-semibold">Waiting for payment confirmation…</div>
            <div className="mt-1 text-xs text-white/50">Do not close this window.</div>
          </div>
        )}

        {step === "success" && paidOrder && (
          <div className="p-6 text-center space-y-4">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/15 border border-emerald-400/40">
              <CheckCircle2 className="h-9 w-9 text-emerald-300" />
            </div>
            <div>
              <div className="text-lg font-extrabold">Payment received!</div>
              <div className="mt-1 text-sm text-white/60">
                Order number: <span className="font-mono font-bold text-white">{paidOrder.order_number}</span>
              </div>
              <div className="mt-1 text-xs text-white/45">
                Save this number — you&apos;ll need it to send your documents on WhatsApp.
              </div>
            </div>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                `Hi UID Infotech, I just paid for *${service.name}* — Order ${paidOrder.order_number}. Here are my documents:`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20bf5c] px-5 py-3 text-sm font-bold text-white w-full"
              data-testid="order-modal-wa-docs"
            >
              <MessageCircle className="h-4 w-4" /> Send documents on WhatsApp
            </a>
            <button onClick={close} className="text-xs text-white/50 hover:text-white/80" data-testid="order-modal-done">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
